import { redirect } from "next/navigation";
import { serverLogger } from "~/logger.server";
import { routes } from "~/routes";
import { AuthError } from "./error";
import { I18nError } from "./i18n-error";

type AsyncFunction<T extends unknown[], R> = (...args: T) => Promise<R>;

type WithAuthErrorHandlerOptions<T extends unknown[]> = {
	logContext?: (...args: T) => Record<string, unknown>;
};

export type ErrorResponse = {
	error: ReturnType<AuthError["toObject"]>;
};

export function withAuthErrorHandler<T extends unknown[]>(
	fn: (...args: T) => Promise<void>,
	options?: WithAuthErrorHandlerOptions<T>,
	// biome-ignore lint/suspicious/noConfusingVoidType: Confusing, but correct
): (...args: T) => Promise<void | ErrorResponse>;

export function withAuthErrorHandler<T extends unknown[], R>(
	fn: AsyncFunction<T, R>,
	options?: WithAuthErrorHandlerOptions<T>,
): (...args: T) => Promise<R | ErrorResponse>;

export function withAuthErrorHandler<T extends unknown[], R>(
	fn: AsyncFunction<T, R> | AsyncFunction<T, void>,
	options?: WithAuthErrorHandlerOptions<T>,
): (...args: T) => Promise<R | ErrorResponse | undefined> {
	return async (...args: T): Promise<R | ErrorResponse | undefined> => {
		try {
			const result = await fn(...args);
			if (result === undefined) {
				return;
			}
			return result;
		} catch (error) {
			if (isRouterError(error)) {
				return;
			}
			const parsedError = new AuthError(error);
			const rawData = parsedError.getRawData();
			if (parsedError.errors?.some((error) => error.code === "email_not_available")) {
				return {
					error: new AuthError(new I18nError("auth:signup.errors.email-already-exists")).toObject(),
				};
			}
			switch (rawData?.code) {
				case "email_verification_required":
				case "invalid_pending_authentication_token":
					return redirect(
						routes.verifyEmail({
							id: rawData.email_verification_id,
							token: rawData.pending_authentication_token,
						}),
					);
				case "mfa_enrollment":
					return redirect(
						routes.mfaEnroll({
							userId: rawData.user.id,
							token: rawData.pending_authentication_token,
						}),
					);
				case "mfa_challenge": {
					const factorId = rawData.authentication_factors[0]?.id;
					if (factorId) {
						return redirect(
							routes.mfa({
								factorId,
								token: rawData.pending_authentication_token,
							}),
						);
					}
					break;
				}
				case "invalid_credentials":
					return {
						error: new AuthError(new I18nError("auth:login.errors.incorrect-email-or-password")).toObject(),
					};
			}
			const isPasswordRejected = parsedError.errors?.some(
				(error) =>
					error.code === "password_too_weak" ||
					error.code === "password_pwned" ||
					error.code === "password_too_short" ||
					error.code === "password_missing_character_type",
			);
			if (isPasswordRejected) {
				return { error: parsedError.toObject() };
			}
			if (parsedError.code === "password_reset_token_not_found") {
				return {
					error: new AuthError(
						new I18nError("auth:reset-password.errors.invalid-or-expired-link"),
					).toObject(),
				};
			}
			if (parsedError.oauthError === "sso_required") {
				return {
					error: new AuthError(new I18nError("auth:login.errors.external-organization")).toObject(),
				};
			}
			serverLogger.error("WorkOS authentication error", error instanceof Error ? error : parsedError, {
				logGroup: "WORKOS_ERROR",
				...options?.logContext?.(...args),
			});
			return { error: parsedError.toObject() };
		}
	};
}

export async function handleErrorOnClient<T>(response: Promise<T | ErrorResponse>): Promise<T> {
	const res = await response;
	if (typeof res === "object" && res !== null && "error" in res) {
		throw new AuthError(res.error);
	}
	return res;
}

export function isRouterError(error: unknown) {
	if (typeof error !== "object" || error === null || !("digest" in error) || typeof error.digest !== "string") {
		return false;
	}

	const digest = error.digest.split(";");
	const [errorCode, type] = digest;
	const destination = digest.slice(2, -2).join(";");
	const status = digest.at(-2);

	const statusCode = Number(status);

	const isRedirectError =
		errorCode === "NEXT_REDIRECT" &&
		(type === "replace" || type === "push") &&
		typeof destination === "string" &&
		!isNaN(statusCode);
	const [prefix] = error.digest.split(";");

	const isHTTPAccessFallbackError = prefix === "NEXT_HTTP_ERROR_FALLBACK";

	return isRedirectError || isHTTPAccessFallbackError;
}
